import React, { useEffect, useState } from "react";
import { Truck, AlertTriangle, FileDown } from "lucide-react";
import CrudModule from "../components/CrudModule";
import { MODULES } from "../lib/modules";
import { api, API, fmtINR, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const CATEGORIES = ["Machinery", "Vehicle", "Bike", "Transport / Truck", "Plant & Equipment", "Computer / IT", "Furniture", "Other"];
const STATUSES = ["In Use", "Under Repair", "Idle", "Sold", "Scrapped"];

const Card = ({ label, value, sub, tone = "text-slate-900", testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-xl font-mono font-semibold tabular-nums mt-2 break-all ${tone}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

export default function AssetsPage() {
  const [s, setS] = useState(null);
  const [regOpen, setRegOpen] = useState(false);
  const [f, setF] = useState({ category: "all", status: "all", location: "", from_date: "", to_date: "" });
  useEffect(() => { api.get("/assets/summary").then((r) => setS(r.data)).catch(() => setS(null)); }, []);

  const download = (useFilters) => {
    const p = new URLSearchParams({ token: localStorage.getItem("token") });
    if (useFilters) {
      if (f.category !== "all") p.set("category", f.category);
      if (f.status !== "all") p.set("status", f.status);
      if (f.location.trim()) p.set("location", f.location.trim());
      if (f.from_date) p.set("from_date", f.from_date);
      if (f.to_date) p.set("to_date", f.to_date);
    }
    window.open(`${API}/assets/register/pdf?${p.toString()}`, "_blank");
  };

  return (
    <div data-testid="assets-page">
      <div className="flex justify-end mb-3">
        <Button data-testid="asset-register-pdf-btn" variant="outline" className="rounded-sm gap-1.5" onClick={() => setRegOpen(true)}>
          <FileDown className="w-4 h-4" /> Download PDF
        </Button>
      </div>
      {s && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-5 h-5 text-[#0B132B]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Asset Value Summary</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <Card testid="asset-count" label="Assets In Use" value={s.asset_count} sub={s.disposed_count ? `${s.disposed_count} sold/scrapped` : "machines, vehicles & equipment"} />
            <Card testid="asset-gross" label="Purchase Value" value={fmtINR(s.gross_value)} sub="total cost of assets held" />
            <Card testid="asset-depreciation" label="Accumulated Depreciation" value={fmtINR(s.accumulated_depreciation)} tone="text-rose-700" sub="charged to P&L so far" />
            <Card testid="asset-book-value" label="Current Book Value" value={fmtINR(s.book_value)} tone="text-emerald-700" sub="after depreciation" />
            <Card testid="asset-monthly-dep" label="Depreciation / Month" value={fmtINR(s.monthly_depreciation)} sub={`${fmtINR(s.yearly_depreciation)} per year`} />
          </div>
          {s.by_category?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="asset-categories">
              {s.by_category.map((c) => (
                <div key={c.category} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-xs">
                  <span className="font-medium text-slate-700">{c.category}</span>
                  <span className="text-slate-400"> · {c.count} · </span>
                  <span className="font-mono text-slate-800">{fmtINR(c.book_value)}</span>
                </div>
              ))}
            </div>
          )}
          {s.alerts?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="asset-alerts">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-900 mb-1"><AlertTriangle className="w-4 h-4" /> Coming up in the next 30 days</div>
              <ul className="text-xs text-amber-900 space-y-0.5">
                {s.alerts.map((a, i) => (
                  <li key={i}>{a.type}: <b>{a.asset_name}</b> ({a.asset_code}) on {fmtDate(a.date)}{a.overdue ? " — overdue" : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <CrudModule config={MODULES.assets} />

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-lg" data-testid="asset-register-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Fixed Asset Register</DialogTitle>
            <DialogDescription>Choose filters for the report, or download the complete register.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="reg-category" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="reg-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Location</label>
              <Input data-testid="reg-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Any location" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchased From</label>
              <Input data-testid="reg-from" type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchased To</label>
              <Input data-testid="reg-to" type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} className="bg-white" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="reg-full-btn" variant="outline" className="rounded-sm" onClick={() => download(false)}>Complete Register</Button>
            <Button data-testid="reg-filtered-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => download(true)}>
              <FileDown className="w-4 h-4" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
